import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Plus, Edit, Trash2, Package, ShoppingBag, Calendar, Users, DollarSign, Clock } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  confirmed: 'bg-blue-100 text-blue-800 border-blue-200',
  completed: 'bg-green-100 text-green-800 border-green-200',
  cancelled: 'bg-red-100 text-red-800 border-red-200',
  processing: 'bg-blue-100 text-blue-800 border-blue-200',
  shipped: 'bg-indigo-100 text-indigo-800 border-indigo-200',
  delivered: 'bg-green-100 text-green-800 border-green-200',
};

const Admin = () => {
  const { isAuthenticated, user, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [cateringBookings, setCateringBookings] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    category: 'hookahs',
    price: '',
    description: '',
    image: '',
    stock: ''
  });

  useEffect(() => {
    if (!isAuthenticated || user?.role !== 'admin') {
      toast.error('Access denied. Admin only.');
      navigate('/');
      return;
    }
    fetchProducts();
    fetchOrders();
    fetchCateringBookings();
  }, [isAuthenticated, user, navigate]);

  const fetchProducts = async () => {
    try {
      const resp = await axios.get(`${API}/products`);
      setProducts(resp.data.products || resp.data || []);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    }
  };

  const fetchOrders = async () => {
    setLoadingOrders(true);
    try {
      const resp = await axios.get(`${API}/orders/all`, {
        headers: { ...getAuthHeader() }
      });
      setOrders(resp.data.orders || []);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    } finally {
      setLoadingOrders(false);
    }
  };

  const fetchCateringBookings = async () => {
    setLoadingBookings(true);
    try {
      const resp = await axios.get(`${API}/catering/bookings`, {
        headers: { ...getAuthHeader() }
      });
      setCateringBookings(resp.data.bookings || []);
    } catch (error) {
      console.error('Failed to fetch catering bookings:', error);
    } finally {
      setLoadingBookings(false);
    }
  };

  const updateBookingStatus = async (booking, newStatus) => {
    try {
      await axios.patch(
        `${API}/catering/bookings/${encodeURIComponent(booking.email)}/${encodeURIComponent(booking.event_date)}/status`,
        { status: newStatus },
        { headers: { ...getAuthHeader() } }
      );
      toast.success(`Booking status updated to ${newStatus}`);
      fetchCateringBookings();
    } catch (error) {
      toast.error('Failed to update booking status');
    }
  };

  const handleAddProduct = () => {
    setEditingProduct(null);
    setFormData({ name: '', category: 'hookahs', price: '', description: '', image: '', stock: '' });
    setIsDialogOpen(true);
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      category: product.category,
      price: product.price,
      description: product.description,
      image: product.image,
      stock: product.stock
    });
    setIsDialogOpen(true);
  };

  const handleSaveProduct = async (e) => {
    e.preventDefault();
    try {
      const productData = {
        ...formData,
        price: parseFloat(formData.price),
        stock: parseInt(formData.stock),
        featured: editingProduct?.featured || false
      };

      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, productData, {
          headers: { ...getAuthHeader() }
        });
        toast.success('Product updated!');
      } else {
        await axios.post(`${API}/products`, productData, {
          headers: { ...getAuthHeader() }
        });
        toast.success('Product added!');
      }
      fetchProducts();
      setIsDialogOpen(false);
    } catch (error) {
      toast.error('Failed to save product');
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Delete this product?')) return;
    try {
      await axios.delete(`${API}/products/${productId}`, {
        headers: { ...getAuthHeader() }
      });
      toast.success('Product deleted!');
      fetchProducts();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const stats = {
    totalProducts: products.length,
    totalOrders: orders.length,
    totalRevenue: orders.reduce((sum, order) => sum + (order.total || 0), 0),
    cateringBookings: cateringBookings.length
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12" data-testid="admin-dashboard">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="font-display text-4xl font-bold text-[#0a1128] mb-2">Admin Dashboard</h1>
          <p className="text-gray-600">Manage your store products, orders, and event bookings</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card data-testid="stat-products">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Products</p>
                  <p className="text-3xl font-bold text-[#0a1128]">{stats.totalProducts}</p>
                </div>
                <Package className="h-10 w-10 text-[#D4AF37]" />
              </div>
            </CardContent>
          </Card>
          <Card data-testid="stat-orders">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Orders</p>
                  <p className="text-3xl font-bold text-[#0a1128]">{stats.totalOrders}</p>
                </div>
                <ShoppingBag className="h-10 w-10 text-[#D4AF37]" />
              </div>
            </CardContent>
          </Card>
          <Card data-testid="stat-revenue">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Revenue</p>
                  <p className="text-3xl font-bold text-[#0a1128]">${stats.totalRevenue.toFixed(2)}</p>
                </div>
                <DollarSign className="h-10 w-10 text-[#D4AF37]" />
              </div>
            </CardContent>
          </Card>
          <Card data-testid="stat-catering">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Event Bookings</p>
                  <p className="text-3xl font-bold text-[#0a1128]">{stats.cateringBookings}</p>
                </div>
                <Calendar className="h-10 w-10 text-[#D4AF37]" />
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="products" className="space-y-6">
          <TabsList data-testid="admin-tabs">
            <TabsTrigger value="products" data-testid="admin-tab-products">Products</TabsTrigger>
            <TabsTrigger value="orders" data-testid="admin-tab-orders">Orders</TabsTrigger>
            <TabsTrigger value="catering" data-testid="admin-tab-catering">Event Bookings</TabsTrigger>
          </TabsList>

          {/* Products Tab */}
          <TabsContent value="products">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Manage Products</CardTitle>
                  <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                    <DialogTrigger asChild>
                      <Button
                        data-testid="add-product-btn"
                        onClick={handleAddProduct}
                        className="bg-[#D4AF37] hover:bg-[#C5A028] text-[#0a1128]"
                      >
                        <Plus className="mr-2 h-4 w-4" />
                        Add Product
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl">
                      <DialogHeader>
                        <DialogTitle>{editingProduct ? 'Edit Product' : 'Add New Product'}</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleSaveProduct} className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Product Name *</label>
                          <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Category *</label>
                            <select className="w-full border border-gray-300 rounded-md px-3 py-2" value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })} required>
                              <option value="hookahs">Hookahs</option>
                              <option value="tobacco">Tobacco</option>
                              <option value="accessories">Accessories</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Price *</label>
                            <Input type="number" step="0.01" value={formData.price} onChange={(e) => setFormData({ ...formData, price: e.target.value })} required />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Description *</label>
                          <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={3} required />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Image URL *</label>
                          <Input value={formData.image} onChange={(e) => setFormData({ ...formData, image: e.target.value })} required />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Stock *</label>
                          <Input type="number" value={formData.stock} onChange={(e) => setFormData({ ...formData, stock: e.target.value })} required />
                        </div>
                        <div className="flex gap-3">
                          <Button type="submit" className="flex-1 bg-[#D4AF37] hover:bg-[#C5A028] text-[#0a1128]">
                            {editingProduct ? 'Update Product' : 'Add Product'}
                          </Button>
                          <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
                        </div>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {products.map((product) => (
                    <div key={product.id} className="flex items-center gap-4 p-4 border rounded-lg">
                      <img src={product.image} alt={product.name} className="h-20 w-20 rounded object-cover" />
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg">{product.name}</h3>
                        <p className="text-sm text-gray-600">{product.category}</p>
                        <div className="flex items-center gap-4 mt-1">
                          <span className="text-[#D4AF37] font-semibold">${product.price}</span>
                          <span className={`text-sm ${product.stock < 10 ? 'text-red-600' : 'text-gray-600'}`}>Stock: {product.stock}</span>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="icon" onClick={() => handleEditProduct(product)}><Edit className="h-4 w-4" /></Button>
                        <Button variant="outline" size="icon" onClick={() => handleDeleteProduct(product.id)} className="text-red-600 hover:bg-red-50"><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </div>
                  ))}
                  {products.length === 0 && (
                    <div className="text-center py-12"><p className="text-gray-600">No products yet</p></div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Orders Tab */}
          <TabsContent value="orders">
            <Card>
              <CardHeader>
                <CardTitle>Recent Orders</CardTitle>
              </CardHeader>
              <CardContent>
                {loadingOrders ? (
                  <div className="text-center py-12"><p className="text-gray-600">Loading orders...</p></div>
                ) : orders.length === 0 ? (
                  <div className="text-center py-12"><p className="text-gray-600">No orders yet</p></div>
                ) : (
                  <div className="space-y-4">
                    {orders.map((order) => (
                      <div key={order.order_id} className="p-4 border rounded-lg" data-testid={`order-${order.order_id}`}>
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <p className="font-semibold">{order.order_id}</p>
                            <p className="text-sm text-gray-600">{order.user_email}</p>
                          </div>
                          <div className="text-right">
                            <span className="text-lg font-bold text-[#D4AF37]">${(order.total || 0).toFixed(2)}</span>
                            <div className="flex gap-2 mt-1">
                              <Badge className={statusColors[order.status] || 'bg-gray-100 text-gray-800'}>{order.status}</Badge>
                              {order.payment_status && (
                                <Badge className={order.payment_status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'}>
                                  {order.payment_status}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="text-sm text-gray-600">
                          <p>Items: {order.items?.length || 0}</p>
                          <p className="text-xs text-gray-400">{order.date ? new Date(order.date).toLocaleString() : ''}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Catering Bookings Tab */}
          <TabsContent value="catering" data-testid="catering-tab-content">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-[#D4AF37]" />
                    Event Catering Bookings
                  </CardTitle>
                  <Button variant="outline" onClick={fetchCateringBookings} data-testid="refresh-bookings-btn">
                    Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {loadingBookings ? (
                  <div className="text-center py-12"><p className="text-gray-600">Loading bookings...</p></div>
                ) : cateringBookings.length === 0 ? (
                  <div className="text-center py-12" data-testid="no-bookings">
                    <Calendar className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-600">No catering bookings yet</p>
                    <p className="text-sm text-gray-400">Bookings from the Event Catering page will appear here</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {cateringBookings.map((booking, idx) => (
                      <div key={idx} className="p-5 border rounded-lg hover:border-[#D4AF37]/50 transition-colors" data-testid={`booking-card-${idx}`}>
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h3 className="font-semibold text-lg text-[#0a1128]">{booking.name}</h3>
                            <p className="text-sm text-gray-500">{booking.email} &middot; {booking.phone}</p>
                          </div>
                          <Badge className={statusColors[booking.status] || 'bg-gray-100 text-gray-800'}>
                            {booking.status}
                          </Badge>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                          <div className="flex items-center gap-2 text-sm">
                            <Calendar className="h-4 w-4 text-[#D4AF37]" />
                            <span>{booking.event_date}</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm">
                            <Clock className="h-4 w-4 text-[#D4AF37]" />
                            <span>{booking.event_type}</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm">
                            <Users className="h-4 w-4 text-[#D4AF37]" />
                            <span>{booking.guest_count} guests</span>
                          </div>
                          <div className="text-sm text-gray-600">
                            {booking.location}
                          </div>
                        </div>

                        {booking.package && (
                          <p className="text-sm mb-2">
                            <span className="font-medium text-[#D4AF37]">Package:</span> {booking.package}
                          </p>
                        )}

                        {booking.special_requests && (
                          <p className="text-sm text-gray-600 mb-3">
                            <span className="font-medium">Notes:</span> {booking.special_requests}
                          </p>
                        )}

                        <div className="flex gap-2 pt-2 border-t">
                          {booking.status !== 'confirmed' && (
                            <Button
                              size="sm"
                              data-testid={`confirm-booking-${idx}`}
                              className="bg-blue-600 hover:bg-blue-700 text-white"
                              onClick={() => updateBookingStatus(booking, 'confirmed')}
                            >
                              Confirm
                            </Button>
                          )}
                          {booking.status !== 'completed' && (
                            <Button
                              size="sm"
                              data-testid={`complete-booking-${idx}`}
                              className="bg-green-600 hover:bg-green-700 text-white"
                              onClick={() => updateBookingStatus(booking, 'completed')}
                            >
                              Complete
                            </Button>
                          )}
                          {booking.status !== 'cancelled' && (
                            <Button
                              size="sm"
                              variant="outline"
                              data-testid={`cancel-booking-${idx}`}
                              className="text-red-600 border-red-200 hover:bg-red-50"
                              onClick={() => updateBookingStatus(booking, 'cancelled')}
                            >
                              Cancel
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Admin;
